import {
  POWERPOINT_ACTIVE_GRAPH_DEFAULT_COORDS,
  buildAddActiveGraphContent,
  getActiveGraphXmlViaIntents,
  setActiveGraphXmlViaIntents,
  type AddActiveGraphCoords,
} from "../../../shared/factset/chartCreatorIntents";
import {
  raiseIntent,
  type IntentAppId,
} from "../../../shared/factset/factsetIntentTransport";
import {
  activatePresentationViaIntents,
  beginUndoScopeViaIntents,
  getActivePresentationRefViaIntents,
  getSelectedShapesViaIntents,
  isFactSetIntentBridgeAvailable,
  setSelectedSlidesViaIntents,
  type FactSetPresentationRef,
  type FactSetSelectedShapeRef,
} from "./intentBridgeService";

const OI_REMOTE_IFRAME_APP_ID: IntentAppId = { appId: "OI Remote Iframe" };

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  if (typeof DOMException === "function") {
    throw new DOMException("PowerPoint ActiveGraph operation was cancelled.", "AbortError");
  }
  const error = new Error("PowerPoint ActiveGraph operation was cancelled.");
  error.name = "AbortError";
  throw error;
}

function presentationRefsMatch(
  expected: Partial<FactSetPresentationRef>,
  active: FactSetPresentationRef | null,
): boolean {
  if (expected.openPresentationId) {
    return active?.openPresentationId === expected.openPresentationId;
  }
  if (expected.fullName) {
    return active?.fullName === expected.fullName;
  }
  return true;
}

async function assertExpectedPresentationIsActive(
  expected: Partial<FactSetPresentationRef> | null | undefined,
): Promise<void> {
  if (!expected?.openPresentationId && !expected?.fullName) return;
  const activePresentationRef = await getActivePresentationRefViaIntents();
  if (!presentationRefsMatch(expected, activePresentationRef)) {
    throw new Error(
      "The active presentation changed before the selected ActiveGraph could be read.",
    );
  }
}

export interface AddPowerPointActiveGraphInput {
  fdsChartXml: string;
  slideId: string;
  presentationRef?: Partial<FactSetPresentationRef> | null;
  coords?: AddActiveGraphCoords;
  signal?: AbortSignal;
  log?: (message: string) => void;
}

export interface AddPowerPointActiveGraphResult {
  message: string;
  selectedShapes: FactSetSelectedShapeRef[];
  selectedActiveGraphXmlConfirmed: boolean;
  selectedActiveGraphXml?: string;
}

export interface ActiveGraphAddDispatchTracker {
  dispatched?: boolean;
}

export async function addPowerPointActiveGraphViaIntents(
  input: AddPowerPointActiveGraphInput,
  tracker?: ActiveGraphAddDispatchTracker,
): Promise<AddPowerPointActiveGraphResult> {
  if (tracker) tracker.dispatched = false;
  throwIfAborted(input.signal);
  const activeGraphContent = buildAddActiveGraphContent(
    input.fdsChartXml,
    input.coords,
    POWERPOINT_ACTIVE_GRAPH_DEFAULT_COORDS,
  );
  if (!isFactSetIntentBridgeAvailable()) {
    throw new Error("FactSet PowerPoint intent bridge is unavailable.");
  }
  const slideId = input.slideId.trim();
  if (!slideId) throw new Error("PowerPoint ActiveGraph create requires a slideId.");
  if (input.presentationRef?.openPresentationId) {
    await activatePresentationViaIntents({
      presentationRef: input.presentationRef,
      log: input.log,
    });
  }
  throwIfAborted(input.signal);
  await setSelectedSlidesViaIntents({
    slideIds: [slideId],
    presentationRef: input.presentationRef,
    log: input.log,
  });
  throwIfAborted(input.signal);
  const closeUndo = await beginUndoScopeViaIntents(input.log, {
    presentationRef: input.presentationRef,
    undoEntryText: "Insert FactSet ActiveGraph",
  });
  try {
    throwIfAborted(input.signal);
    if (tracker) tracker.dispatched = true;
    const context = await raiseIntent(
      "factset.Presentation.AddActiveGraph",
      {
        type: "factset.presentation.addActiveGraph",
        activeGraphContent,
      },
      30_000,
      OI_REMOTE_IFRAME_APP_ID,
      input.signal,
    );
    const message =
      context &&
      typeof context === "object" &&
      typeof (context as Record<string, unknown>).message === "string"
        ? ((context as Record<string, unknown>).message as string)
        : "Active graph added successfully";
    let selectedShapes: FactSetSelectedShapeRef[] = [];
    let selectedActiveGraphXmlConfirmed = false;
    let selectedActiveGraphXml: string | undefined;
    try {
      await assertExpectedPresentationIsActive(input.presentationRef);
      const selected = await getSelectedShapesViaIntents({ log: input.log });
      selectedShapes = selected.shapes;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") throw error;
    }
    if (
      selectedShapes.length === 1 &&
      selectedShapes[0].slideId === slideId
    ) {
      try {
        const readBack = await getActiveGraphXmlViaIntents({
          signal: input.signal,
        });
        const readBackXml = readBack.fdsChartXml?.trim();
        throwIfAborted(input.signal);
        await assertExpectedPresentationIsActive(input.presentationRef);
        throwIfAborted(input.signal);
        const rechecked = await getSelectedShapesViaIntents({ log: input.log });
        if (
          readBackXml &&
          rechecked.shapes.length === 1 &&
          rechecked.shapes[0].id === selectedShapes[0].id &&
          rechecked.shapes[0].slideId === selectedShapes[0].slideId
        ) {
          selectedShapes = rechecked.shapes;
          selectedActiveGraphXml = readBackXml;
          selectedActiveGraphXmlConfirmed = true;
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") throw error;
        selectedActiveGraphXmlConfirmed = false;
      }
    }
    return {
      message,
      selectedShapes,
      selectedActiveGraphXmlConfirmed,
      ...(selectedActiveGraphXml ? { selectedActiveGraphXml } : {}),
    };
  } finally {
    await closeUndo();
  }
}

export async function getSelectedPowerPointActiveGraph(
  options: {
    signal?: AbortSignal;
    presentationRef?: Partial<FactSetPresentationRef> | null;
  } = {},
): Promise<{
  shape: FactSetSelectedShapeRef;
  fdsChartXml: string;
}> {
  throwIfAborted(options.signal);
  await assertExpectedPresentationIsActive(options.presentationRef);
  throwIfAborted(options.signal);
  const selected = await getSelectedShapesViaIntents();
  if (selected.shapes.length !== 1) {
    throw new Error(
      "Select exactly one FactSet ActiveGraph with a single click and try again.",
    );
  }
  const result = await getActiveGraphXmlViaIntents({
    signal: options.signal,
  });
  if (!result.fdsChartXml?.trim()) {
    throw new Error(
      "The selected shape is not a readable FactSet ActiveGraph.",
    );
  }
  throwIfAborted(options.signal);
  await assertExpectedPresentationIsActive(options.presentationRef);
  throwIfAborted(options.signal);
  const rechecked = await getSelectedShapesViaIntents();
  if (
    rechecked.shapes.length !== 1 ||
    rechecked.shapes[0].id !== selected.shapes[0].id ||
    rechecked.shapes[0].slideId !== selected.shapes[0].slideId
  ) {
    throw new Error(
      "The selected ActiveGraph changed while it was being inspected.",
    );
  }
  return { shape: rechecked.shapes[0], fdsChartXml: result.fdsChartXml };
}

export async function setSelectedPowerPointActiveGraphXml(
  fdsChartXml: string,
  options: { refresh?: boolean; signal?: AbortSignal } = {},
): Promise<string> {
  return setActiveGraphXmlViaIntents(fdsChartXml, options);
}
